import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Search, Download, Upload, X, Mail, Building2, Globe, IdCard, Users, Mic2, Shield, RefreshCw, Printer, Plus, Phone, Pencil, Copy, Trash2, Check, XCircle } from "lucide-react";
import CsvImportWizard from "@/components/attendees/CsvImportWizard";
import AttendeeSpreadsheet from "@/components/attendees/AttendeeSpreadsheet";
import DedupeDialog from "@/components/attendees/DedupeDialog";
import AttendeeSortableTh from "@/components/attendees/AttendeeSortableTh";
import DeleteAttendeesDialog from "@/components/attendees/DeleteAttendeesDialog";
import { bulkDeleteAttendees, deleteAttendee, exportCsvUrl, fetchAllAttendeesInView, fetchTicketTypes } from "@/lib/attendeesApi";
import { fetchAiSearch, normalizeSearchRow } from "@/lib/ai-search-api";
import { scoreAttendees, loadAttendeeScores } from "@/lib/ai-segments-api";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";
import { loadTicketEngine, syncTicketEngine } from "@/lib/ticket-engine";
import { useEventMeta } from "@/lib/event-meta";
import { formatInTimezone, useWorkspaceTimezone } from "@/lib/timezone";
import { fetchBadgeTemplate, fetchBadgeDataSources } from "@/lib/badge-template";
import BadgePrintModal from "@/components/BadgePrintModal";
import { API_BASE } from "@/lib/utils";
import WorkspaceMediaField from "@/components/WorkspaceMediaField";

const statusColor = {
  Paid: "var(--primary)",
  Registered: "#60A5FA",
  Pending: "#E25D3A",
  "Pending Approval": "#F59E0B",
  Rejected: "#EF4444",
  Refunded: "#71717A",
  "Checked In": "#34D399",
  Invited: "#A78BFA",
};

// const API_BASE = typeof window !== "undefined" ? "" : "http://localhost:8001";
const PAGE_SIZE = 50;
const ACTIVITY_POLL_MS = 10 * 60 * 1000;

function formatUploadedAt(iso, timezone) {
  return formatInTimezone(iso, timezone, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const STAFF_ROLES = [
  { value: "showcaller", label: "Showcaller" },
  { value: "usher", label: "Usher" },
  { value: "gate_usher", label: "Gate usher" },
  { value: "production", label: "Production" },
  { value: "other", label: "Other" },
];

export default function Attendees() {
  const { id } = useParams();
  const e = useEventMeta(id);
  const displayTimezone = useWorkspaceTimezone();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/admin/capabilities`, { cache: "no-store" });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        const flags = data?.featureFlags || {};
        const features = data?.features || [];
        setAiSearchEnabled(flags.ai_search === true || features.includes("ai_search"));
      } catch {
        if (!cancelled) setAiSearchEnabled(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      try {
        const scores = await loadAttendeeScores(id);
        if (cancelled || !scores) return;
        const map = {};
        Object.entries(scores).forEach(([aid, row]) => {
          map[aid] = row?.engagementScore ?? row?.score;
        });
        setScoreMap((prev) => ({ ...map, ...prev }));
      } catch {
        /* optional */
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const [tab, setTab] = useState("attendees");
  const [page, setPage] = useState(0);
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [rows, setRows] = useState([]);
  const [listTotal, setListTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [approvalBusy, setApprovalBusy] = useState({});
  const [segment, setSegment] = useState("invitees");
  const [ticketFilter, setTicketFilter] = useState("all");
  const [ticketTypeOptions, setTicketTypeOptions] = useState([]);
  const [sortBy, setSortBy] = useState("updated_at");
  const [sortDir, setSortDir] = useState("desc");
  const [bulkApprovalBusy, setBulkApprovalBusy] = useState(false);

  const [importOpen, setImportOpen] = useState(false);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [dedupeOpen, setDedupeOpen] = useState(false);
  const [spreadsheetRows, setSpreadsheetRows] = useState([]);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, ids: [], names: [] });
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [selectAllMatching, setSelectAllMatching] = useState(false);
  const [selectAllLoading, setSelectAllLoading] = useState(false);
  const [selectionStatus, setSelectionStatus] = useState({});
  const [aiSearchEnabled, setAiSearchEnabled] = useState(false);
  const [scoreBusy, setScoreBusy] = useState(false);
  const [scoreMap, setScoreMap] = useState({});
  const [aiMatches, setAiMatches] = useState([]);
  const [aiMatchesLoading, setAiMatchesLoading] = useState(false);
  const headerSelectRef = useRef(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualBusy, setManualBusy] = useState(false);
  const [manualForm, setManualForm] = useState({
    name: "",
    email: "",
    company: "",
    role: "",
    ticket: "",
    country: "",
    status: "Invited",
  });

  const [staffList, setStaffList] = useState([]);
  const [staffBusy, setStaffBusy] = useState(false);
  const [staffForm, setStaffForm] = useState({
    id: "",
    name: "",
    email: "",
    role_type: "usher",
    badge_label: "",
    notes: "",
    access_pin: "",
  });
  const [staffPinClear, setStaffPinClear] = useState(false);

  const [speakersList, setSpeakersList] = useState([]);
  const [speakerBusy, setSpeakerBusy] = useState(false);
  const [speakerForm, setSpeakerForm] = useState({
    id: "",
    name: "",
    title: "",
    company: "",
    bio: "",
    website: "",
    linkedin: "",
    email: "",
    photo: "",
    kind: "speaker",
    sort_order: 0,
  });

  const [ticketLabels, setTicketLabels] = useState([]);

  const [selected, setSelected] = useState([]);
  const [open, setOpen] = useState(null);
  const [edit, setEdit] = useState(null);
  const [activity, setActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedCustomColumns, setSelectedCustomColumns] = useState([]);
  const [badgePrint, setBadgePrint] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    setPage(0);
  }, [debouncedQ]);

  useEffect(() => {
    if (!aiSearchEnabled || !debouncedQ.trim()) {
      setAiMatches([]);
      setAiMatchesLoading(false);
      return undefined;
    }
    let cancelled = false;
    const controller = new AbortController();
    setAiMatchesLoading(true);
    (async () => {
      try {
        const result = await fetchAiSearch(debouncedQ.trim(), {
          scopes: ["attendees"],
          eventId: id,
          limit: 6,
          signal: controller.signal,
        });
        if (cancelled) return;
        setAiMatches((result.items || []).map(normalizeSearchRow));
      } catch {
        if (!cancelled) setAiMatches([]);
      } finally {
        if (!cancelled) setAiMatchesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [aiSearchEnabled, debouncedQ, id]);

  useEffect(() => {
    setPage(0);
  }, [segment, sortBy, sortDir, ticketFilter]);

  useEffect(() => {
    setSelectAllMatching(false);
    setSelected([]);
    setSelectionStatus({});
  }, [debouncedQ, segment, sortBy, sortDir, ticketFilter]);

  useEffect(() => {
    let cancel = false;
    (async () => {
      await syncTicketEngine(e.id);
      const eng = loadTicketEngine(e.id);
      const tiers = (eng.tickets || []).filter((t) => t.type === "TIER").map((t) => t.name);
      if (!cancel) setTicketLabels(tiers.length ? tiers : ["Regular", "Early Bird", "VIP"]);
    })();
    return () => {
      cancel = true;
    };
  }, [e.id]);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const tickets = await fetchTicketTypes(id);
        if (!cancel) setTicketTypeOptions(tickets);
      } catch {
        if (!cancel) setTicketTypeOptions([]);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [id]);

  const fetchAttendees = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
        offset: String(page * PAGE_SIZE),
        include_answers: "1",
      });
      if (debouncedQ.trim()) params.set("q", debouncedQ.trim());
      if (segment && segment !== "all") params.set("segment", segment);
      if (ticketFilter && ticketFilter !== "all") params.set("ticket", ticketFilter);
      const coreSort = ["name", "email", "company", "ticket", "status", "country", "mobile", "created_at", "updated_at"];
      if (sortBy === "engagement_score") {
        params.set("sort", "updated_at");
      } else if (sortBy && coreSort.includes(sortBy)) params.set("sort", sortBy);
      else if (sortBy) {
        params.set("sort_custom_field", sortBy);
        params.set("sort", "name");
      }
      if (sortDir) params.set("sort_dir", sortDir);
      const res = await fetch(`${API_BASE}/api/events/${id}/attendees?${params}`);
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      // FormSG/Plumber people are mirrored into attendees on ingest — do not
      // also merge gov-attendee refs here (that double-counted empty-email rows).
      const attendees = Array.isArray(data.attendees) ? data.attendees : [];
      const total = Number(data.total) || 0;
      setRows(attendees);
      setListTotal(total);
    } catch (err) {
      console.error(err);
      toast.error("Could not load attendees", { description: "Check your connection and try again." });
      setRows([]);
      setListTotal(0);
    } finally {
      setLoading(false);
    }
  }, [id, page, debouncedQ, segment, ticketFilter, sortBy, sortDir]);

  const loadRowsForSpreadsheet = useCallback(async () => {
    if (!selected.length) {
      setSpreadsheetRows(rows);
      return rows;
    }
    const loaded = [];
    for (let i = 0; i < selected.length; i += 500) {
      const chunk = selected.slice(i, i + 500);
      const params = new URLSearchParams({ limit: "500", include_answers: "1", ids: chunk.join(",") });
      const res = await fetch(`${API_BASE}/api/events/${id}/attendees?${params}`);
      if (!res.ok) throw new Error("Could not load selected attendees");
      const data = await res.json();
      if (Array.isArray(data.attendees)) loaded.push(...data.attendees);
    }
    setSpreadsheetRows(loaded);
    return loaded;
  }, [id, selected, rows]);

  const toggleSort = (key) => {
    setSortBy((prev) => {
      if (prev === key) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        return prev;
      }
      setSortDir(key === "engagement_score" ? "desc" : "asc");
      return key;
    });
  };

  const displayRows = useMemo(() => {
    if (sortBy !== "engagement_score") return rows;
    const dir = sortDir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const sa = scoreMap[a.id];
      const sb = scoreMap[b.id];
      const na = sa == null ? -1 : Number(sa);
      const nb = sb == null ? -1 : Number(sb);
      return (na - nb) * dir;
    });
  }, [rows, sortBy, sortDir, scoreMap]);

  const openDeleteDialog = (ids) => {
    const idList = [...new Set(ids.filter(Boolean))];
    if (!idList.length) return;
    const fromRows = rows.filter((r) => idList.includes(r.id));
    const names = fromRows.map((r) => `${r.name || "Unnamed"} · ${r.email || ""}`);
    setDeleteDialog({ open: true, ids: idList, names });
  };

  const runConfirmedDelete = async () => {
    const { ids } = deleteDialog;
    if (!ids.length) return;
    setDeleteBusy(true);
    try {
      if (ids.length === 1) {
        await deleteAttendee(id, ids[0]);
      } else {
        await bulkDeleteAttendees(id, ids);
      }
      toast.success(ids.length === 1 ? "Attendee deleted" : `Deleted ${ids.length} attendees`);
      setDeleteDialog({ open: false, ids: [], names: [] });
      if (selectAllMatching && ids.length >= selected.length) {
        clearSelection();
      } else {
        setSelected((s) => s.filter((x) => !ids.includes(x)));
        setSelectionStatus((prev) => {
          const next = { ...prev };
          for (const rid of ids) delete next[rid];
          return next;
        });
      }
      if (open && ids.includes(open.id)) setOpen(null);
      await fetchAttendees();
    } catch (err) {
      toast.error("Delete failed", { description: String(err.message || err) });
    } finally {
      setDeleteBusy(false);
    }
  };

  useEffect(() => {
    fetchAttendees();
  }, [fetchAttendees]);

  const fetchStaff = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/events/${id}/staff`);
      if (!res.ok) return;
      const data = await res.json();
      setStaffList(data.staff || []);
    } catch {
      /* ignore */
    }
  }, [id]);

  const fetchSpeakers = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/events/${id}/speakers`);
      if (!res.ok) return;
      const data = await res.json();
      setSpeakersList(data.speakers || []);
    } catch {
      /* ignore */
    }
  }, [id]);

  useEffect(() => {
    if (tab === "staff") fetchStaff();
    if (tab === "speakers") fetchSpeakers();
  }, [tab, fetchStaff, fetchSpeakers]);

  const fetchActivity = useCallback(
    async (attendeeId) => {
      setActivityLoading(true);
      try {
        const res = await fetch(`${API_BASE}/api/events/${id}/attendees/${attendeeId}/activity`);
        if (!res.ok) throw new Error("activity failed");
        const data = await res.json();
        setActivity(data.items || []);
      } catch {
        setActivity([]);
      } finally {
        setActivityLoading(false);
      }
    },
    [id],
  );

  useEffect(() => {
    if (!open?.id) return;
    fetchActivity(open.id);
    const timer = setInterval(() => fetchActivity(open.id), ACTIVITY_POLL_MS);
    return () => clearInterval(timer);
  }, [open?.id, fetchActivity]);

  useEffect(() => {
    if (open) {
      setEdit({ ...open });
    } else {
      setEdit(null);
    }
  }, [open]);

  const totalPages = Math.max(1, Math.ceil(listTotal / PAGE_SIZE));
  const rangeStart = listTotal === 0 ? 0 : page * PAGE_SIZE + 1;
  const rangeEnd = Math.min(listTotal, (page + 1) * PAGE_SIZE);

  const filteredForSelect = useMemo(() => rows, [rows]);
  const pageIds = useMemo(() => filteredForSelect.map((a) => a.id).filter(Boolean), [filteredForSelect]);
  const allPageSelected = pageIds.length > 0 && pageIds.every((aid) => selected.includes(aid));
  const selectedCount = selectAllMatching ? listTotal : selected.length;

  const pendingApprovalCount = useMemo(
    () => rows.filter((r) => String(r.status || "") === "Pending Approval").length,
    [rows],
  );

  const selectedPendingIds = useMemo(() => {
    if (selectAllMatching && segment === "pending_approval") return selected;
    return selected.filter((sid) => {
      const fromRow = rows.find((r) => r.id === sid);
      const status = fromRow?.status ?? selectionStatus[sid];
      return String(status || "") === "Pending Approval";
    });
  }, [selected, rows, selectAllMatching, segment, selectionStatus]);

  useEffect(() => {
    const el = headerSelectRef.current;
    if (!el) return;
    el.indeterminate = Boolean(!selectAllMatching && allPageSelected && listTotal > pageIds.length);
  }, [selectAllMatching, allPageSelected, listTotal, pageIds.length]);

  const applyBulkStatus = async (nextStatus) => {
    if (!selectedPendingIds.length) return;
    setBulkApprovalBusy(true);
    try {
      for (const aid of selectedPendingIds) {
        await patchAttendee(aid, { status: nextStatus });
      }
      toast.success(
        nextStatus === "Paid"
          ? `Approved ${selectedPendingIds.length} registration(s)`
          : `Rejected ${selectedPendingIds.length} registration(s)`,
      );
      clearSelection();
      setOpen(null);
      await fetchAttendees();
    } catch {
      toast.error("Could not complete bulk update");
      fetchAttendees();
    } finally {
      setBulkApprovalBusy(false);
    }
  };
  const customFieldKeys = useMemo(() => {
    const s = new Set();
    for (const row of rows) {
      const answers = row.customAnswers || {};
      for (const key of Object.keys(answers)) s.add(key);
    }
    return Array.from(s).sort();
  }, [rows]);

  const clearSelection = () => {
    setSelectAllMatching(false);
    setSelected([]);
    setSelectionStatus({});
  };

  const toggleAll = () => {
    if (selectAllMatching) {
      clearSelection();
      return;
    }
    if (allPageSelected) {
      clearSelection();
      return;
    }
    setSelectAllMatching(false);
    const nextStatus = { ...selectionStatus };
    for (const row of filteredForSelect) {
      if (row.id) nextStatus[row.id] = row.status;
    }
    setSelectionStatus(nextStatus);
    setSelected(pageIds);
  };

  const selectAllInCurrentView = async () => {
    if (listTotal <= pageIds.length) return;
    setSelectAllLoading(true);
    try {
      const items = await fetchAllAttendeesInView(id, {
        q: debouncedQ,
        segment,
        ticket: ticketFilter,
        sortBy,
        sortDir,
      });
      const ids = items.map((item) => item.id).filter(Boolean);
      const statusMap = Object.fromEntries(items.map((item) => [item.id, item.status]));
      setSelected(ids);
      setSelectionStatus(statusMap);
      setSelectAllMatching(true);
      toast.success(`Selected all ${ids.length.toLocaleString()} attendees in this view`);
    } catch (err) {
      toast.error("Could not select all attendees", { description: String(err.message || err) });
    } finally {
      setSelectAllLoading(false);
    }
  };

  const toggle = (aid) => {
    setSelectAllMatching(false);
    const row = rows.find((r) => r.id === aid);
    setSelected((s) => {
      if (s.includes(aid)) {
        setSelectionStatus((prev) => {
          const next = { ...prev };
          delete next[aid];
          return next;
        });
        return s.filter((x) => x !== aid);
      }
      if (row?.id) {
        setSelectionStatus((prev) => ({ ...prev, [aid]: row.status }));
      }
      return [...s, aid];
    });
  };

  const patchAttendee = async (attendeeId, body) => {
    const res = await fetch(`${API_BASE}/api/events/${id}/attendees/${attendeeId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error("update failed");
    return res.json();
  };

  const openProfileBadgePrint = async (ev, profile, subjectType = "attendee") => {
    ev.stopPropagation();
    if (!profile?.id) {
      toast.error("Cannot print badge", { description: "Profile id is missing." });
      return;
    }
    try {
      const [template, sources] = await Promise.all([fetchBadgeTemplate(id), fetchBadgeDataSources(id)]);
      const key = subjectType === "staff" ? "staff" : subjectType === "vip" ? "vip" : "attendee";
      const sample = { ...(sources[key]?.sample || {}), ...profile };
      setBadgePrint({
        open: true,
        template,
        dataSources: { ...sources, [key]: { ...sources[key], sample } },
        subjectType: key,
        subjectId: profile.id,
      });
    } catch {
      toast.error("Could not load badge template");
    }
  };

  const applyApprovalStatus = async (attendeeId, nextStatus) => {
    setApprovalBusy((prev) => ({ ...prev, [attendeeId]: true }));
    setRows((rs) => rs.map((r) => (r.id === attendeeId ? { ...r, status: nextStatus } : r)));
    setOpen((o) => (o?.id === attendeeId ? { ...o, status: nextStatus } : o));
    setEdit((x) => (x?.id === attendeeId ? { ...x, status: nextStatus } : x));
    try {
      await patchAttendee(attendeeId, { status: nextStatus });
      toast.success(nextStatus === "Paid" ? "Registration approved" : "Registration rejected");
    } catch {
      toast.error("Could not update approval status");
      fetchAttendees();
    } finally {
      setApprovalBusy((prev) => ({ ...prev, [attendeeId]: false }));
    }
  };

  const saveDrawer = async () => {
    if (!edit?.id) return;
    setSaving(true);
    try {
      await patchAttendee(edit.id, {
        name: edit.name,
        email: edit.email,
        company: edit.company,
        role: edit.role,
        ticket: edit.ticket,
        status: edit.status,
        country: edit.country,
        seat: edit.seat,
        custom_answers: edit.customAnswers || {},
      });
      toast.success("Attendee updated");
      setOpen((o) => (o ? { ...edit } : null));
      fetchAttendees();
      fetchActivity(edit.id);
    } catch {
      toast.error("Could not save changes");
    } finally {
      setSaving(false);
    }
  };

  const openBulkEdit = async () => {
    if (!selected.length) {
      toast.error("Select at least one attendee to bulk edit");
      return;
    }
    try {
      await loadRowsForSpreadsheet();
      setBulkEditOpen(true);
    } catch {
      toast.error("Could not load selected rows");
    }
  };

  const openExport = async () => {
    if (selected.length) {
      try {
        await loadRowsForSpreadsheet();
        setExportOpen(true);
      } catch {
        toast.error("Could not load selected rows");
      }
      return;
    }
    const cols = ["email", "name", "mobile", "company", "role", "ticket", "country", "status", ...customFieldKeys];
    window.open(exportCsvUrl(id, { columns: cols }), "_blank", "noopener,noreferrer");
    toast.success("Downloading full attendee export");
  };

  const createManualAttendee = async () => {
    if (!manualForm.name.trim() || !manualForm.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    setManualBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/events/${id}/attendees`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(manualForm),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Could not create attendee");
      toast.success("Attendee added");
      setManualOpen(false);
      setManualForm({ name: "", email: "", company: "", role: "", ticket: "", country: "", status: "Invited" });
      fetchAttendees();
    } catch (err) {
      toast.error("Create failed", { description: String(err.message || err) });
    } finally {
      setManualBusy(false);
    }
  };

  const saveStaff = async () => {
    if (!staffForm.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setStaffBusy(true);
    try {
      const body = {
        id: staffForm.id || undefined,
        name: staffForm.name.trim(),
        email: staffForm.email.trim() || undefined,
        role_type: staffForm.role_type,
        badge_label: staffForm.badge_label.trim() || undefined,
        notes: staffForm.notes.trim() || undefined,
      };
      if (staffForm.access_pin.trim()) body.access_pin = staffForm.access_pin.trim();
      else if (staffForm.id && staffPinClear) body.access_pin = "";
      const res = await fetch(`${API_BASE}/api/events/${id}/staff`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error();
      toast.success(staffForm.id ? "Staff updated" : "Staff added");
      setStaffForm({ id: "", name: "", email: "", role_type: "usher", badge_label: "", notes: "", access_pin: "" });
      setStaffPinClear(false);
      fetchStaff();
    } catch {
      toast.error("Could not save staff");
    } finally {
      setStaffBusy(false);
    }
  };

  const deleteStaff = async (sid) => {
    if (!window.confirm("Remove this staff member?")) return;
    try {
      const res = await fetch(`${API_BASE}/api/events/${id}/staff/${sid}`, { method: "DELETE" });
      if (!res.ok) throw new Error();
      toast.success("Removed");
      fetchStaff();
    } catch {
      toast.error("Delete failed");
    }
  };

  const saveSpeaker = async () => {
    if (!speakerForm.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setSpeakerBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/events/${id}/speakers`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: speakerForm.id || undefined,
          name: speakerForm.name.trim(),
          title: speakerForm.title.trim() || undefined,
          company: speakerForm.company.trim() || undefined,
          bio: speakerForm.bio.trim() || undefined,
          website: speakerForm.website.trim() || undefined,
          linkedin: speakerForm.linkedin.trim() || undefined,
          email: speakerForm.email.trim() || undefined,
          photo: speakerForm.photo.trim() || undefined,
          kind: speakerForm.kind,
          sort_order: Number(speakerForm.sort_order) || 0,
        }),
      });
      if (!res.ok) throw new Error();
      toast.success(speakerForm.id ? "Speaker updated" : "Speaker added");
      setSpeakerForm({
        id: "",
        name: "",
        title: "",
        company: "",
        bio: "",
        website: "",
        linkedin: "",
        email: "",
        photo: "",
        kind: "speaker",
        sort_order: 0,
      });
      fetchSpeakers();
    } catch {
      toast.error("Could not save speaker / VIP");
    } finally {
      setSpeakerBusy(false);
    }
  };

  const deleteSpeaker = async (sid) => {
    if (!window.confirm("Remove this entry?")) return;
    try {
      const res = await fetch(`${API_BASE}/api/events/${id}/speakers/${sid}`, { method: "DELETE" });
      if (!res.ok) throw new Error();
      toast.success("Removed");
      fetchSpeakers();
    } catch {
      toast.error("Delete failed");
    }
  };

  const editSpeaker = (s) => {
    setSpeakerForm({
      id: s.id,
      name: s.name || "",
      title: s.title || "",
      company: s.company || "",
      bio: s.bio || "",
      website: s.website || "",
      linkedin: s.linkedin || "",
      email: s.email || "",
      photo: s.photo || "",
      kind: s.kind || "speaker",
      sort_order: s.sort_order ?? 0,
    });
  };

  const editStaffMember = (s) => {
    setStaffPinClear(false);
    setStaffForm({
      id: s.id,
      name: s.name || "",
      email: s.email || "",
      role_type: s.role_type || "usher",
      badge_label: s.badge_label || "",
      notes: s.notes || "",
      access_pin: "",
    });
  };

  return (
    <div className="p-8 lg:p-12 max-w-[1500px] mx-auto" data-testid="attendees-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-overline">{e.title} · people</div>
          <h1 className="font-heading text-4xl mt-2 tracking-tight font-medium">
            {(tab === "staff" ? staffList.length : tab === "speakers" ? speakersList.length : listTotal).toLocaleString()}{" "}
            {tab === "attendees" ? "attendees" : tab === "staff" ? "staff" : "speakers / VIP"}
          </h1>
          <div className="text-xs text-[var(--text-secondary)] font-mono mt-2">
            {tab === "attendees" && !loading
              ? `Showing ${rangeStart.toLocaleString()}–${rangeEnd.toLocaleString()} of ${listTotal.toLocaleString()}`
              : null}
            {tab === "attendees" ? " · List view with CSV import/export support." : null}
            {tab === "staff" ? " · Badge labels surface on printed credentials." : null}
            {tab === "speakers" ? " · Used to prepopulate the Page Builder speakers block." : null}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            type="button"
            className={`btn-secondary text-xs border ${tab === "attendees" ? "ring-1 ring-[var(--primary)] border-[var(--primary)]/50" : "border-[var(--border-default)]"}`}
            onClick={() => setTab("attendees")}
          >
            <Users size={14} /> Attendees
          </button>
          <button
            type="button"
            className={`btn-secondary text-xs border ${tab === "staff" ? "ring-1 ring-[var(--primary)] border-[var(--primary)]/50" : "border-[var(--border-default)]"}`}
            onClick={() => setTab("staff")}
          >
            <Shield size={14} /> Staff &amp; badges
          </button>
          <button
            type="button"
            className={`btn-secondary text-xs border ${tab === "speakers" ? "ring-1 ring-[var(--primary)] border-[var(--primary)]/50" : "border-[var(--border-default)]"}`}
            onClick={() => setTab("speakers")}
          >
            <Mic2 size={14} /> Speakers / VIP
          </button>
          {tab === "attendees" ? (
            <>
              <button type="button" className="btn-secondary" data-testid="import-csv" onClick={() => setImportOpen(true)}>
                <Upload size={14} /> Import CSV
              </button>
              <button type="button" className="btn-secondary" onClick={() => setManualOpen(true)}>
                <Plus size={14} /> Add attendee
              </button>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => window.open(`${API_BASE}/api/events/${id}/attendees/import-template.csv`, "_blank", "noopener,noreferrer")}
              >
                <Download size={14} /> CSV template
              </button>
              <button
                type="button"
                className="btn-secondary"
                disabled={!selected.length}
                onClick={openBulkEdit}
              >
                <Pencil size={14} /> Bulk edit
              </button>
              <button type="button" className="btn-secondary" data-testid="export-csv" onClick={openExport}>
                <Download size={14} /> Export CSV
              </button>
              <button type="button" className="btn-secondary" onClick={() => setDedupeOpen(true)}>
                <Copy size={14} /> Find duplicates
              </button>
            </>
          ) : null}
        </div>
      </div>

      {tab === "attendees" && (
        <>
          {(segment === "pending_approval" ? listTotal > 0 : pendingApprovalCount > 0) ? (
            <div className="surface p-4 mt-6 flex flex-wrap items-center justify-between gap-3 border border-[#F59E0B]/40">
              <div className="text-sm text-[#FCD34D] font-mono">
                {segment === "pending_approval"
                  ? `${listTotal.toLocaleString()} pending approval`
                  : `${pendingApprovalCount} pending on this page · use Pending approval segment for full list`}
              </div>
              <div className="text-xs text-[var(--text-secondary)]">
                Approve confirms as Paid. Reject marks the registration declined.
              </div>
            </div>
          ) : null}
          <div className="mt-8 flex flex-wrap gap-2" role="tablist" aria-label="Attendee segments">
            {[
              { key: "invitees", label: "Guest list (invitees)" },
              { key: "all", label: "All records" },
              { key: "invited_not_registered", label: "Invited · not registered" },
              { key: "pending_approval", label: "Pending approval" },
            ].map((s) => (
              <button
                key={s.key}
                type="button"
                role="tab"
                aria-selected={segment === s.key}
                className={`btn-secondary text-xs ${segment === s.key ? "ring-1 ring-[var(--primary)]" : ""}`}
                onClick={() => setSegment(s.key)}
                data-testid={s.key === "pending_approval" ? "holding-list-toggle" : undefined}
              >
                {s.label}
              </button>
            ))}
            {ticketTypeOptions.length > 0 ? (
              <select
                className="input-dark text-xs ml-auto max-w-[200px]"
                value={ticketFilter}
                onChange={(ev) => setTicketFilter(ev.target.value)}
                aria-label="Filter by ticket type"
                data-testid="attendees-ticket-filter"
              >
                <option value="all">All ticket types</option>
                {ticketTypeOptions.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            ) : null}
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <div className="flex flex-1 min-w-[220px] items-center gap-2 border border-[var(--border-default)] bg-[var(--bg-surface)] p-2">
              <Search size={14} className="ml-2 text-[var(--text-tertiary)]" />
              <input
                value={q}
                onChange={(ev) => setQ(ev.target.value)}
                placeholder="Search attendees by name, email, company, mobile…"
                className="bg-transparent flex-1 px-2 py-1.5 text-sm focus:outline-none"
                data-testid="attendees-search"
              />
              {loading ? <span className="text-[10px] font-mono text-[var(--text-tertiary)] pr-2">Loading…</span> : null}
              {aiSearchEnabled ? (
                <>
                  <button
                    type="button"
                    className="btn-secondary text-xs shrink-0"
                    disabled={scoreBusy}
                    onClick={async () => {
                      setScoreBusy(true);
                      try {
                        const body = await scoreAttendees(id, 500);
                        const next = {};
                        for (const row of body.attendees || []) {
                          if (row.id) next[row.id] = row.engagementScore;
                        }
                        setScoreMap(next);
                        toast.success("Engagement scores updated", { description: `${body.count || 0} attendees scored` });
                      } catch (err) {
                        toast.error(err?.message || "Could not score attendees");
                      } finally {
                        setScoreBusy(false);
                      }
                    }}
                    data-testid="attendees-score-btn"
                  >
                    {scoreBusy ? "Scoring…" : "Score"}
                  </button>
                  <Link
                    to="/admin/ai"
                    className="text-[9px] font-mono uppercase tracking-widest px-2 py-1 border border-[var(--primary)]/40 text-[var(--primary)] shrink-0 hover:bg-[var(--primary)]/5"
                    title="Open AI Search"
                  >
                    AI
                  </Link>
                </>
              ) : null}
            </div>
          </div>

          {aiSearchEnabled && debouncedQ.trim() && (aiMatchesLoading || aiMatches.length > 0) ? (
            <div className="mt-3 surface p-3 border border-[var(--primary)]/25" data-testid="attendees-ai-matches">
              <div className="flex items-center justify-between gap-2 mb-2">
                <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--primary)]">
                  AI semantic matches
                </span>
                <Link to="/admin/ai" className="text-[10px] font-mono text-[var(--text-tertiary)] hover:text-[var(--primary)]">
                  Open AI Search
                </Link>
              </div>
              {aiMatchesLoading ? (
                <p className="text-xs text-[var(--text-tertiary)]">Finding related attendees…</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {aiMatches.map((row) => (
                    <Link
                      key={row.id}
                      to={row.href}
                      className="text-xs px-2.5 py-1 border border-[var(--border-default)] hover:border-[var(--primary)]/40"
                    >
                      {row.title}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ) : null}

          {allPageSelected && listTotal > pageIds.length && !selectAllMatching ? (
            <div className="mt-4 surface p-3 px-4 flex flex-wrap items-center gap-2 text-sm border border-[var(--primary)]/30">
              <span className="font-mono text-[var(--text-secondary)]">
                All {pageIds.length} on this page are selected.
              </span>
              <button
                type="button"
                className="text-[var(--primary)] font-mono text-xs underline underline-offset-2 hover:no-underline disabled:opacity-50"
                disabled={selectAllLoading}
                onClick={selectAllInCurrentView}
                data-testid="select-all-attendees-in-view"
              >
                {selectAllLoading ? "Selecting…" : `Select all ${listTotal.toLocaleString()} in this view`}
              </button>
            </div>
          ) : null}

          {selectAllMatching && listTotal > 0 ? (
            <div className="mt-4 surface p-3 px-4 flex flex-wrap items-center gap-2 text-sm border border-[var(--primary)]/40">
              <span className="font-mono text-[#FCD34D]">
                All {listTotal.toLocaleString()} attendees in this view are selected.
              </span>
              <button type="button" className="text-xs font-mono text-[var(--text-tertiary)] underline" onClick={clearSelection}>
                Clear selection
              </button>
            </div>
          ) : null}

          {selected.length > 0 && (
            <div className="mt-4 surface p-3 px-4 flex flex-wrap items-center justify-between gap-3">
              <div className="text-sm font-mono">{selectedCount.toLocaleString()} selected</div>
              <div className="flex flex-wrap items-center gap-2">
                {selectedPendingIds.length > 0 ? (
                  <>
                    <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-tertiary)]">
                      {selectedPendingIds.length} pending approval
                    </span>
                    <button
                      type="button"
                      className="btn-primary text-xs h-9 px-3"
                      disabled={bulkApprovalBusy}
                      onClick={() => applyBulkStatus("Paid")}
                    >
                      {bulkApprovalBusy ? "Working…" : "Approve selected"}
                    </button>
                    <button
                      type="button"
                      className="btn-ghost text-xs h-9 px-3 border border-[#EF4444]/50 text-[#EF4444]"
                      disabled={bulkApprovalBusy}
                      onClick={() => applyBulkStatus("Rejected")}
                    >
                      Reject selected
                    </button>
                  </>
                ) : null}
                <button
                  type="button"
                  className="btn-ghost text-xs h-9 px-3 border border-[#EF4444]/50 text-[#EF4444]"
                  onClick={() => openDeleteDialog(selected)}
                  data-testid="delete-selected-attendees"
                >
                  <Trash2 size={14} /> Delete selected
                </button>
              </div>
            </div>
          )}

          <div className="mt-6 surface overflow-x-auto min-w-0 max-w-full overscroll-x-contain">
            {customFieldKeys.length ? (
              <div className="p-3 border-b border-[var(--border-default)] flex items-center gap-2 flex-wrap">
                <span className="text-overline">Custom columns</span>
                {customFieldKeys.map((k) => (
                  <label key={k} className="text-xs font-mono text-[var(--text-secondary)] flex items-center gap-1">
                    <input
                      type="checkbox"
                      className="accent-[var(--primary)]"
                      checked={selectedCustomColumns.includes(k)}
                      onChange={(ev) =>
                        setSelectedCustomColumns((prev) =>
                          ev.target.checked ? [...prev, k] : prev.filter((x) => x !== k),
                        )
                      }
                    />
                    {k}
                  </label>
                ))}
              </div>
            ) : null}
            <table className="w-full min-w-[980px] text-sm">
              <thead className="border-b border-[var(--border-default)] bg-[var(--bg-elev)]">
                <tr className="text-left text-[var(--text-tertiary)] font-mono text-[10px] uppercase tracking-widest">
                  <th className="p-4 w-10">
                    <input
                      ref={headerSelectRef}
                      type="checkbox"
                      className="accent-[var(--primary)]"
                      onChange={toggleAll}
                      checked={
                        selectAllMatching || (pageIds.length > 0 && allPageSelected)
                      }
                      disabled={pageIds.length === 0 && !selectAllMatching}
                      aria-label="Select attendees"
                    />
                  </th>
                  <AttendeeSortableTh label="Attendee" field="name" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                  <AttendeeSortableTh label="Company" field="company" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                  <AttendeeSortableTh label="Ticket" field="ticket" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                  <AttendeeSortableTh label="Status" field="status" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                  {aiSearchEnabled ? (
                    <AttendeeSortableTh label="Score" field="engagement_score" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                  ) : null}
                  <AttendeeSortableTh label="Country" field="country" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                  <AttendeeSortableTh label="Mobile" field="mobile" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                  <AttendeeSortableTh label="Uploaded" field="created_at" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                  {selectedCustomColumns.map((k) => (
                    <AttendeeSortableTh key={k} label={k} field={k} sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                  ))}
                  <th className="p-4 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {displayRows.map((a) => (
                  <tr
                    key={a.id}
                    className="border-b border-[var(--border-default)] hover:bg-[var(--bg-elev)] transition cursor-pointer"
                    onClick={() => setOpen(a)}
                    data-testid={`attendee-row-${a.id}`}
                  >
                    <td className="p-4" onClick={(ev) => ev.stopPropagation()}>
                      <input
                        type="checkbox"
                        className="accent-[var(--primary)]"
                        checked={selectAllMatching || selected.includes(a.id)}
                        onChange={() => toggle(a.id)}
                      />
                    </td>
                    <td className="p-4 min-w-0">
                      <div className="flex items-center gap-3 min-w-0">
                        <Avatar className="w-9 h-9 shrink-0 rounded-none border border-[var(--border-default)]">
                          <AvatarFallback className="bg-[var(--bg-elev)] text-[var(--text-primary)] rounded-none text-xs">
                            {String(a.name || "")
                              .split(" ")
                              .filter(Boolean)
                              .map((n) => n[0])
                              .join("")
                              .slice(0, 2) || "?"}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                          <div className="font-medium text-[var(--text-primary)] truncate">{a.name || "Unnamed attendee"}</div>
                          <div className="text-xs text-[var(--text-tertiary)] font-mono truncate" title={a.email}>
                            {a.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="p-4 min-w-0">
                      <div className="text-[var(--text-primary)] truncate" title={a.company}>
                        {a.company || "—"}
                      </div>
                      <div className="text-xs text-[var(--text-tertiary)] truncate" title={a.role}>
                        {a.role || "—"}
                      </div>
                    </td>
                    <td className="p-4 font-mono text-xs min-w-0">
                      <span className="block truncate" title={a.ticket}>
                        {a.ticket || "—"}
                      </span>
                    </td>
                    <td className="p-4 align-top min-w-0">
                      <div className="flex flex-col gap-1.5 min-w-0 max-w-full">
                        <select
                          className="w-full max-w-full bg-transparent font-mono text-[10px] uppercase tracking-widest px-2 py-1 border truncate"
                          style={{ color: statusColor[a.status] || "#A1A1AA", borderColor: (statusColor[a.status] || "#71717A") + "55" }}
                          value={a.status}
                          onChange={async (ev) => {
                            const nextStatus = ev.target.value;
                            setRows((rs) => rs.map((r) => (r.id === a.id ? { ...r, status: nextStatus } : r)));
                            try {
                              await patchAttendee(a.id, { status: nextStatus });
                            } catch {
                              toast.error("Could not update status");
                              fetchAttendees();
                            }
                          }}
                          onClick={(ev) => ev.stopPropagation()}
                        >
                          {Object.keys(statusColor).map((s) => (
                            <option key={s} value={s} className="text-[var(--on-primary)]">
                              {s}
                            </option>
                          ))}
                        </select>
                        {String(a.status || "") === "Pending Approval" ? (
                          <div className="flex items-center gap-1 shrink-0" onClick={(ev) => ev.stopPropagation()}>
                            <button
                              type="button"
                              className="inline-flex h-8 w-8 items-center justify-center rounded border border-[#34D399]/50 text-[#34D399] hover:bg-[#34D399]/10 disabled:opacity-50"
                              disabled={Boolean(approvalBusy[a.id])}
                              onClick={() => applyApprovalStatus(a.id, "Paid")}
                              aria-label="Approve registration"
                              title="Approve (mark as Paid)"
                            >
                              <Check size={15} strokeWidth={2.5} />
                            </button>
                            <button
                              type="button"
                              className="inline-flex h-8 w-8 items-center justify-center rounded border border-[#EF4444]/50 text-[#EF4444] hover:bg-[#EF4444]/10 disabled:opacity-50"
                              disabled={Boolean(approvalBusy[a.id])}
                              onClick={() => applyApprovalStatus(a.id, "Rejected")}
                              aria-label="Reject registration"
                              title="Reject registration"
                            >
                              <XCircle size={15} strokeWidth={2.5} />
                            </button>
                          </div>
                        ) : null}
                      </div>
                    </td>
                    {aiSearchEnabled ? (
                      <td className="p-4 font-mono text-xs text-[var(--text-secondary)]" title={scoreMap[a.id] != null ? `Engagement score ${scoreMap[a.id]}` : undefined}>
                        {scoreMap[a.id] != null ? scoreMap[a.id] : "—"}
                      </td>
                    ) : null}
                    <td className="hidden xl:table-cell p-4 font-mono text-xs text-[var(--text-secondary)] min-w-0">
                      <span className="block truncate" title={a.country}>
                        {a.country || "—"}
                      </span>
                    </td>
                    <td className="hidden lg:table-cell p-4 font-mono text-xs text-[var(--text-secondary)] min-w-0">
                      <span className="block truncate" title={a.mobile}>
                        {a.mobile || "—"}
                      </span>
                    </td>
                    <td className="hidden lg:table-cell p-4 font-mono text-xs text-[var(--text-secondary)] whitespace-nowrap">
                      {formatUploadedAt(a.createdAt || a.updatedAt, displayTimezone)}
                    </td>
                    {selectedCustomColumns.map((k) => (
                      <td key={`${a.id}-${k}`} className="p-4 text-xs text-[var(--text-secondary)]">{formatAnswerValue((a.customAnswers || {})[k])}</td>
                    ))}
                    <td className="p-4 text-right text-[var(--text-tertiary)]" onClick={(ev) => ev.stopPropagation()}>
                      <button
                        type="button"
                        className="inline-flex p-2 rounded-lg border border-transparent hover:border-[var(--border-default)] hover:text-[var(--primary)] text-[var(--text-secondary)] transition"
                        title="Preview & print badge"
                        onClick={(ev) => openProfileBadgePrint(ev, a, "attendee")}
                        data-testid={`attendee-print-badge-${a.id}`}
                      >
                        <Printer size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
                {!loading && rows.length === 0 ? (
                  <tr>
                    <td colSpan={9 + selectedCustomColumns.length} className="p-8 text-center text-sm text-[var(--text-tertiary)]">
                      No attendees found{debouncedQ ? ` for "${debouncedQ}"` : ""}.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>

          <div className="mt-6 flex items-center justify-between text-sm font-mono text-[var(--text-tertiary)]">
            <button
              type="button"
              className="btn-ghost text-xs"
              disabled={page <= 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              Previous
            </button>
            <span>
              Page {page + 1} / {totalPages}
            </span>
            <button
              type="button"
              className="btn-ghost text-xs"
              disabled={page + 1 >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </>
      )}

      {tab === "staff" && (
        <div className="mt-10 space-y-8">
          <div className="surface p-6 space-y-4">
            <div className="text-overline">{staffForm.id ? "Edit staff" : "Add staff"} — badge for visibility; optional PIN required at gate check-in when operator is selected.</div>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Name *">
                <input className="input-dark" value={staffForm.name} onChange={(ev) => setStaffForm((f) => ({ ...f, name: ev.target.value }))} />
              </Field>
              <Field label="Email">
                <input className="input-dark" type="email" value={staffForm.email} onChange={(ev) => setStaffForm((f) => ({ ...f, email: ev.target.value }))} />
              </Field>
              <Field label="Role">
                <select
                  className="input-dark"
                  value={staffForm.role_type}
                  onChange={(ev) => setStaffForm((f) => ({ ...f, role_type: ev.target.value }))}
                >
                  {STAFF_ROLES.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Badge label">
                <input
                  className="input-dark"
                  placeholder="Shown on lanyard"
                  value={staffForm.badge_label}
                  onChange={(ev) => setStaffForm((f) => ({ ...f, badge_label: ev.target.value }))}
                />
              </Field>
              <Field label="Notes">
                <input className="input-dark" value={staffForm.notes} onChange={(ev) => setStaffForm((f) => ({ ...f, notes: ev.target.value }))} />
              </Field>
              <Field label="Check-in PIN (optional)">
                <input
                  type="password"
                  className="input-dark font-mono"
                  autoComplete="new-password"
                  placeholder={staffForm.id ? "New PIN — leave blank to keep" : "4–8 digits for gate kiosks"}
                  value={staffForm.access_pin}
                  onChange={(ev) => setStaffForm((f) => ({ ...f, access_pin: ev.target.value }))}
                />
              </Field>
            </div>
            {staffForm.id ? (
              <label className="flex items-center gap-2 text-xs text-[var(--text-secondary)]">
                <input
                  type="checkbox"
                  className="accent-[var(--primary)]"
                  checked={staffPinClear}
                  onChange={(ev) => setStaffPinClear(ev.target.checked)}
                />
                Remove gate PIN on save
              </label>
            ) : null}
            <div className="flex gap-2">
              <button type="button" className="btn-primary" disabled={staffBusy} onClick={saveStaff}>
                {staffForm.id ? "Save changes" : "Add staff"}
              </button>
              {staffForm.id ? (
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={() => {
                    setStaffForm({ id: "", name: "", email: "", role_type: "usher", badge_label: "", notes: "", access_pin: "" });
                    setStaffPinClear(false);
                  }}
                >
                  Cancel edit
                </button>
              ) : null}
            </div>
          </div>

          <div className="surface overflow-x-auto">
            <table className="w-full text-sm min-w-[600px]">
              <thead className="border-b border-[var(--border-default)] bg-[var(--bg-elev)]">
                <tr className="text-left text-[var(--text-tertiary)] font-mono text-[10px] uppercase tracking-widest">
                  <th className="p-4">Badge</th>
                  <th className="p-4">Name</th>
                  <th className="p-4">Role</th>
                  <th className="p-4">Email</th>
                  <th className="p-4">Gate PIN</th>
                  <th className="p-4 w-28"></th>
                </tr>
              </thead>
              <tbody>
                {staffList.map((s) => (
                  <tr key={s.id} className="border-b border-[var(--border-default)] hover:bg-[var(--bg-elev)]">
                    <td className="p-4">
                      <span className="inline-flex items-center px-2 py-1 text-[10px] font-mono uppercase tracking-widest bg-[var(--primary)]/15 text-[var(--primary)] border border-[var(--primary)]/40">
                        {s.badge_label || s.name?.slice(0, 12)}
                      </span>
                    </td>
                    <td className="p-4 font-medium">{s.name}</td>
                    <td className="p-4 font-mono text-xs text-[var(--text-secondary)]">{s.role_type}</td>
                    <td className="p-4 font-mono text-xs">{s.email}</td>
                    <td className="p-4 font-mono text-[10px] text-[var(--text-tertiary)]">{s.has_pin ? "Set" : "—"}</td>
                    <td className="p-4 text-right space-x-2">
                      <button
                        type="button"
                        className="btn-ghost text-xs"
                        onClick={(ev) => openProfileBadgePrint(ev, s, "staff")}
                        data-testid={`staff-print-badge-${s.id}`}
                      >
                        <Printer size={14} /> Print
                      </button>
                      <button type="button" className="btn-ghost text-xs" onClick={() => editStaffMember(s)}>
                        Edit
                      </button>
                      <button type="button" className="btn-ghost text-xs text-[#FF453A]" onClick={() => deleteStaff(s.id)}>
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {staffList.length === 0 ? <div className="p-8 text-sm text-[var(--text-tertiary)] text-center">No staff yet — add show callers, ushers, or gate staff.</div> : null}
          </div>
        </div>
      )}

      {tab === "speakers" && (
        <div className="mt-10 space-y-8">
          <p className="text-sm text-[var(--text-secondary)] max-w-2xl">
            Speakers and invited VIP profiles feed the Page Builder speakers block. Use for keynotes, panels, and hosts across conferences, seminars, tours, and concerts.
          </p>
          <div className="surface p-6 space-y-4">
            <div className="text-overline">{speakerForm.id ? "Edit entry" : "Add speaker or VIP"}</div>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Name *">
                <input className="input-dark" value={speakerForm.name} onChange={(ev) => setSpeakerForm((f) => ({ ...f, name: ev.target.value }))} />
              </Field>
              <Field label="Kind">
                <select className="input-dark" value={speakerForm.kind} onChange={(ev) => setSpeakerForm((f) => ({ ...f, kind: ev.target.value }))}>
                  <option value="speaker">Speaker</option>
                  <option value="vvip">Invited VIP</option>
                  <option value="host">Host / MC</option>
                </select>
              </Field>
              <Field label="Title">
                <input className="input-dark" value={speakerForm.title} onChange={(ev) => setSpeakerForm((f) => ({ ...f, title: ev.target.value }))} />
              </Field>
              <Field label="Company / org">
                <input className="input-dark" value={speakerForm.company} onChange={(ev) => setSpeakerForm((f) => ({ ...f, company: ev.target.value }))} />
              </Field>
              <Field label="Email">
                <input className="input-dark" type="email" value={speakerForm.email} onChange={(ev) => setSpeakerForm((f) => ({ ...f, email: ev.target.value }))} />
              </Field>
              <Field label="Sort order">
                <input className="input-dark" type="number" value={speakerForm.sort_order} onChange={(ev) => setSpeakerForm((f) => ({ ...f, sort_order: ev.target.value }))} />
              </Field>
              <Field label="Bio">
                <textarea className="input-dark" rows={3} value={speakerForm.bio} onChange={(ev) => setSpeakerForm((f) => ({ ...f, bio: ev.target.value }))} />
              </Field>
              <Field label="Website">
                <input className="input-dark" value={speakerForm.website} onChange={(ev) => setSpeakerForm((f) => ({ ...f, website: ev.target.value }))} />
              </Field>
              <Field label="LinkedIn URL">
                <input className="input-dark" value={speakerForm.linkedin} onChange={(ev) => setSpeakerForm((f) => ({ ...f, linkedin: ev.target.value }))} />
              </Field>
              <WorkspaceMediaField
                label="Headshot photo"
                hint="Upload a portrait or pick from the media library. Shown on the public speakers grid."
                value={speakerForm.photo}
                onChange={(url) => setSpeakerForm((f) => ({ ...f, photo: url || "" }))}
                testId="speaker-headshot-upload"
              />
            </div>
            <div className="flex gap-2">
              <button type="button" className="btn-primary" disabled={speakerBusy} onClick={saveSpeaker}>
                {speakerForm.id ? "Save changes" : "Add"}
              </button>
              {speakerForm.id ? (
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={() =>
                    setSpeakerForm({
                      id: "",
                      name: "",
                      title: "",
                      company: "",
                      bio: "",
                      website: "",
                      linkedin: "",
                      email: "",
                      photo: "",
                      kind: "speaker",
                      sort_order: 0,
                    })
                  }
                >
                  Cancel edit
                </button>
              ) : null}
            </div>
          </div>

          <div className="surface overflow-x-auto">
            <table className="w-full text-sm min-w-[600px]">
              <thead className="border-b border-[var(--border-default)] bg-[var(--bg-elev)]">
                <tr className="text-left text-[var(--text-tertiary)] font-mono text-[10px] uppercase tracking-widest">
                  <th className="p-4">Name</th>
                  <th className="p-4">Subtitle</th>
                  <th className="p-4">Kind</th>
                  <th className="p-4 w-28"></th>
                </tr>
              </thead>
              <tbody>
                {speakersList.map((s) => (
                  <tr key={s.id} className="border-b border-[var(--border-default)] hover:bg-[var(--bg-elev)]">
                    <td className="p-4 font-medium">{s.name}</td>
                    <td className="p-4 text-xs text-[var(--text-secondary)]">
                      {[s.title, s.company].filter(Boolean).join(" · ")}
                    </td>
                    <td className="p-4 font-mono text-[10px] uppercase">{s.kind}</td>
                    <td className="p-4 text-right space-x-2">
                      <button
                        type="button"
                        className="btn-ghost text-xs"
                        onClick={(ev) => openProfileBadgePrint(ev, s, "vip")}
                        data-testid={`speaker-print-badge-${s.id}`}
                      >
                        <Printer size={14} /> Print
                      </button>
                      <button type="button" className="btn-ghost text-xs" onClick={() => editSpeaker(s)}>
                        Edit
                      </button>
                      <button type="button" className="btn-ghost text-xs text-[#FF453A]" onClick={() => deleteSpeaker(s.id)}>
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {speakersList.length === 0 ? <div className="p-8 text-sm text-[var(--text-tertiary)] text-center">No speakers yet.</div> : null}
          </div>
        </div>
      )}

      <CsvImportWizard
        eventId={id}
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onSuccess={() => {
          setImportOpen(false);
          fetchAttendees();
        }}
      />
      <AttendeeSpreadsheet
        eventId={id}
        mode="bulk"
        open={bulkEditOpen}
        onClose={() => setBulkEditOpen(false)}
        rows={spreadsheetRows}
        selectedIds={selected}
        customFieldKeys={customFieldKeys}
        onSaved={() => {
          setSelected([]);
          fetchAttendees();
        }}
      />
      <AttendeeSpreadsheet
        eventId={id}
        mode="export"
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        rows={spreadsheetRows}
        selectedIds={selected}
        customFieldKeys={customFieldKeys}
      />
      <DedupeDialog
        eventId={id}
        open={dedupeOpen}
        onClose={() => setDedupeOpen(false)}
        onSuccess={fetchAttendees}
      />
      <DeleteAttendeesDialog
        open={deleteDialog.open}
        count={deleteDialog.ids.length}
        namesPreview={deleteDialog.names}
        busy={deleteBusy}
        onClose={() => !deleteBusy && setDeleteDialog({ open: false, ids: [], names: [] })}
        onConfirm={runConfirmedDelete}
      />

      {manualOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" role="dialog">
          <div className="surface max-w-lg w-full p-6 border border-[var(--border-default)] shadow-xl">
            <div className="flex justify-between items-start gap-4">
              <div>
                <div className="font-heading text-xl">Add attendee manually</div>
                <p className="text-xs text-[var(--text-tertiary)] mt-2">Create one attendee directly without CSV upload.</p>
              </div>
              <button type="button" className="btn-ghost p-2" aria-label="Close" onClick={() => setManualOpen(false)}>
                <X size={18} />
              </button>
            </div>
            <div className="mt-4 grid md:grid-cols-2 gap-3">
              <input className="input-dark" placeholder="Name" value={manualForm.name} onChange={(e) => setManualForm((s) => ({ ...s, name: e.target.value }))} />
              <input className="input-dark" placeholder="Email" type="email" value={manualForm.email} onChange={(e) => setManualForm((s) => ({ ...s, email: e.target.value }))} />
              <input className="input-dark" placeholder="Company" value={manualForm.company} onChange={(e) => setManualForm((s) => ({ ...s, company: e.target.value }))} />
              <input className="input-dark" placeholder="Role" value={manualForm.role} onChange={(e) => setManualForm((s) => ({ ...s, role: e.target.value }))} />
              <input className="input-dark" placeholder="Ticket" value={manualForm.ticket} onChange={(e) => setManualForm((s) => ({ ...s, ticket: e.target.value }))} />
              <input className="input-dark" placeholder="Country" value={manualForm.country} onChange={(e) => setManualForm((s) => ({ ...s, country: e.target.value }))} />
              <select className="input-dark md:col-span-2" value={manualForm.status} onChange={(e) => setManualForm((s) => ({ ...s, status: e.target.value }))}>
                {Object.keys(statusColor).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" className="btn-ghost" onClick={() => setManualOpen(false)}>Cancel</button>
              <button type="button" className="btn-primary" disabled={manualBusy} onClick={createManualAttendee}>
                {manualBusy ? "Saving..." : "Create attendee"}
              </button>
            </div>
          </div>
        </div>
      )}

      <Sheet open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <SheetContent side="right" className="bg-[var(--bg-surface)] border-[var(--border-default)] text-[var(--text-primary)] w-full sm:w-[480px] sm:max-w-[480px] flex flex-col overflow-hidden p-0">
          {open && edit && (
            <div data-testid="attendee-drawer" className="flex flex-col h-full">
            <div className="flex-1 overflow-y-auto p-6">
              <SheetHeader>
                <div className="flex items-center gap-4">
                  <Avatar className="w-14 h-14 rounded-none border border-[var(--border-default)]">
                    <AvatarFallback className="bg-[var(--primary)] text-[var(--on-primary)] rounded-none">
                      {String(edit.name || "")
                        .split(" ")
                        .filter(Boolean)
                        .map((n) => n[0])
                        .join("")
                        .slice(0, 2) || "?"}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <SheetTitle className="font-heading text-2xl text-[var(--text-primary)] tracking-tight">Administrator edit</SheetTitle>
                    <div className="text-xs font-mono text-[var(--text-tertiary)] mt-1">{edit.role || "—"} · {edit.company || "—"}</div>
                  </div>
                </div>
              </SheetHeader>

              {String(edit.status || "") === "Pending Approval" ? (
                <div className="mt-6 surface p-4 flex flex-wrap items-center justify-between gap-3 border border-[#F59E0B]/40">
                  <div className="text-xs text-[var(--text-secondary)]">
                    This registration is waiting in the holding list.
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="btn-primary text-xs h-9 px-4"
                      disabled={Boolean(approvalBusy[edit.id]) || saving}
                      onClick={() => applyApprovalStatus(edit.id, "Paid")}
                    >
                      Approve
                    </button>
                    <button
                      type="button"
                      className="btn-ghost text-xs h-9 px-4 border border-[#EF4444]/50 text-[#EF4444]"
                      disabled={Boolean(approvalBusy[edit.id]) || saving}
                      onClick={() => applyApprovalStatus(edit.id, "Rejected")}
                    >
                      Reject
                    </button>
                  </div>
                </div>
              ) : null}

              <div className="mt-8 space-y-3">
                <Field label="Full name">
                  <input className="input-dark" value={edit.name} onChange={(ev) => setEdit((x) => ({ ...x, name: ev.target.value }))} />
                </Field>
                <Field label="Email">
                  <input className="input-dark" type="email" value={edit.email} onChange={(ev) => setEdit((x) => ({ ...x, email: ev.target.value }))} />
                </Field>
                <Field label="Company">
                  <input className="input-dark" value={edit.company} onChange={(ev) => setEdit((x) => ({ ...x, company: ev.target.value }))} />
                </Field>
                <Field label="Role">
                  <input className="input-dark" value={edit.role} onChange={(ev) => setEdit((x) => ({ ...x, role: ev.target.value }))} />
                </Field>
                <Field label="Country">
                  <input className="input-dark" value={edit.country} onChange={(ev) => setEdit((x) => ({ ...x, country: ev.target.value }))} />
                </Field>
                <Field label="Ticket (upgrade / change)">
                  <select
                    className="input-dark"
                    value={edit.ticket}
                    onChange={(ev) => setEdit((x) => ({ ...x, ticket: ev.target.value }))}
                  >
                    {[edit.ticket, ...ticketLabels].filter((v, i, arr) => v && arr.indexOf(v) === i).map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Seat">
                  <input
                    className="input-dark font-mono"
                    placeholder="e.g. A-12"
                    value={edit.seat || ""}
                    onChange={(ev) => setEdit((x) => ({ ...x, seat: ev.target.value }))}
                  />
                </Field>
                <Field label="Status">
                  <select
                    className="input-dark"
                    value={edit.status}
                    onChange={(ev) => setEdit((x) => ({ ...x, status: ev.target.value }))}
                  >
                    {Object.keys(statusColor).map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </Field>
                <div className="surface p-3 space-y-2">
                  <div className="text-overline">Custom form answers</div>
                  {Object.keys(edit.customAnswers || {}).length ? (
                    Object.entries(edit.customAnswers || {}).map(([k, v]) => (
                      <Field key={k} label={k}>
                        <input
                          className="input-dark"
                          value={formatAnswerValue(v)}
                          onChange={(ev) =>
                            setEdit((x) => ({
                              ...x,
                              customAnswers: { ...(x.customAnswers || {}), [k]: ev.target.value },
                            }))
                          }
                        />
                      </Field>
                    ))
                  ) : (
                    <div className="text-xs text-[var(--text-tertiary)]">No submitted custom answers.</div>
                  )}
                </div>
              </div>

              <div className="mt-8 surface p-5">
                <div className="text-overline mb-3 flex items-center justify-between gap-2">
                  <span>Activity timeline</span>
                  <span className="text-[10px] font-mono text-[var(--text-tertiary)]">Live · poll every 10 min</span>
                </div>
                {activityLoading && activity.length === 0 ? (
                  <div className="text-xs text-[var(--text-tertiary)]">Loading…</div>
                ) : (
                  <div className="space-y-3 text-sm">
                    {activity.length === 0 ? (
                      <div className="text-xs text-[var(--text-tertiary)]">No activity yet.</div>
                    ) : (
                      activity.map((item, idx) => (
                        <div key={`${item.ts}-${idx}`} className="flex gap-3">
                          <span className="font-mono text-[10px] text-[var(--text-tertiary)] w-32 mt-0.5 shrink-0">{item.label}</span>
                          <span className="text-[var(--text-primary)]">{item.message}</span>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>

              <div className="mt-6 space-y-3 border-t border-[var(--border-default)] pt-6">
                <RowRO icon={Mail} k="Email" v={edit.email} />
                <RowRO icon={Phone} k="Mobile" v={edit.mobile} />
                <RowRO icon={Building2} k="Company" v={edit.company} />
                <RowRO icon={IdCard} k="Ticket" v={edit.ticket} />
                <RowRO icon={Globe} k="Country" v={edit.country} />
              </div>
              </div>

              <div className="shrink-0 bg-[var(--bg-surface)] border-t border-[var(--border-default)] p-4 flex flex-col gap-2">
                <div className="flex gap-2">
                <button type="button" className="btn-primary flex-1 justify-center" disabled={saving} onClick={saveDrawer}>
                  {saving ? "Saving…" : "Save changes"}
                </button>
                <button
                  type="button"
                  className="btn-secondary flex-1 justify-center gap-1"
                  onClick={() => open?.id && fetchActivity(open.id)}
                  title="Refresh now (timeline also refreshes every 10 minutes)"
                >
                  <RefreshCw size={14} /> Refresh log
                </button>
                </div>
                <button
                  type="button"
                  className="btn-ghost w-full justify-center gap-1 border border-[#EF4444]/50 text-[#EF4444]"
                  disabled={saving || deleteBusy}
                  onClick={() => edit?.id && openDeleteDialog([edit.id])}
                  data-testid="delete-attendee-drawer"
                >
                  <Trash2 size={14} /> Delete attendee
                </button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {badgePrint?.open && badgePrint.template && badgePrint.dataSources ? (
        <BadgePrintModal
          open={badgePrint.open}
          onClose={() => setBadgePrint(null)}
          eventId={id}
          template={badgePrint.template}
          dataSources={badgePrint.dataSources}
          subjectType={badgePrint.subjectType}
          subjectId={badgePrint.subjectId}
          eventTitle={e.title}
        />
      ) : null}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-overline">{label}</label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function RowRO({ icon: Icon, k, v }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-dashed border-[var(--border-default)] text-xs">
      <span className="text-overline flex items-center gap-2">
        <Icon size={12} /> {k}
      </span>
      <span className="font-mono text-right max-w-[220px] truncate">{v}</span>
    </div>
  );
}

function formatAnswerValue(value) {
  if (Array.isArray(value)) return value.join(" | ");
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (value && typeof value === "object") return JSON.stringify(value);
  return String(value ?? "");
}
